import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MdCreate, MdSettings } from "react-icons/md";
import { RiFileExcel2Fill } from "react-icons/ri";
import { useSnackbar } from "notistack";
import DatePicker, { DateObject, Value } from "react-multi-date-picker";
import { useParams } from "react-router-dom";
import { endpoints } from "../../../api/endpoints";
import { IGenerateReportJsonResponse } from "../../../api/types/reports/IGenerateReportJsonResponse";
import { IReportInfo } from "../../../api/types/reports/IReportInfo";
import "../../../components/select/css/green.css";
import { Color } from "../../../constants/colors";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { showModal } from "../../../store/modals/modalsSlice";
import { getTicketGridInfo } from "../../../store/ticketGrids/thunks/getTicketGridInfo";
import {
  canEditTicketGridPrices,
  canManageTicketGridReport,
} from "../../../utils/userPermissions";
import { TicketGridReportCell } from "./TicketGridReportCell";
import { TicketGridReportNotesPanel } from "./TicketGridReportNotesPanel";
import { reportTableTheme, reportRowHoverSx, reportCellBackground, priceCellInnerSx, reportBodyCellTdSx } from "./reportTableTheme";
import { decodeHtmlEntities } from "./utils/decodeHtmlEntities";
import {
  buildEmptyPriceEditDraft,
  buildPriceEditDraft,
  buildPriceEditPayload,
  PriceEditDraft,
} from "./utils/buildPriceEditDraft";
import {
  getOperatorNamesFromStats,
  resolveOperatorCell,
} from "./utils/resolveOperatorCell";

const weekDays = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
const months = [
  "Янв",
  "Фев",
  "Мар",
  "Апр",
  "Май",
  "Июн",
  "Июл",
  "Авг",
  "Сен",
  "Окт",
  "Ноя",
  "Дек",
];

const API_DATE_FORMAT = "YYYY-MM-DD";
const DISPLAY_DATE_FORMAT = "DD.MM.YYYY";

function todayApiDate(): string {
  return new DateObject().format(API_DATE_FORMAT);
}

function isPastApiDate(apiDate: string): boolean {
  return apiDate < todayApiDate();
}

function isSelectableReportDate(apiDate: string): boolean {
  return apiDate <= todayApiDate();
}

const headerCellSx = {
  border: reportTableTheme.border,
  minHeight: reportTableTheme.cellMinHeight,
  maxHeight: reportTableTheme.cellMaxHeight,
  py: reportTableTheme.cellPaddingY,
  px: reportTableTheme.cellPaddingX,
  fontWeight: 700,
  fontSize: "11px",
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  color: "#000000",
  backgroundColor: reportTableTheme.headerBg,
  whiteSpace: "normal" as const,
  overflowWrap: "break-word" as const,
  wordBreak: "break-word" as const,
  position: "sticky" as const,
  top: 0,
  zIndex: 2,
};

const operatorHeaderCellSx = {
  ...headerCellSx,
  fontSize: "13px",
  fontWeight: 700,
};

const footerCellSx = {
  border: reportTableTheme.border,
  minHeight: reportTableTheme.cellMinHeight,
  maxHeight: reportTableTheme.cellMaxHeight,
  py: reportTableTheme.cellPaddingY,
  px: reportTableTheme.cellPaddingX,
  backgroundColor: "#ffffff",
  position: "sticky" as const,
  bottom: 0,
  zIndex: 2,
};

function parseAvailableNights(nights: string): string[] {
  return nights
    .split(",")
    .map((night) => night.trim())
    .filter((night) => night.length > 0);
}

export function TicketGridReportView() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const ticketGridTemplates = useAppSelector((s) => s.ticketGrids.templates ?? []);
  const userType = useAppSelector((s) => s.user.info?.type);
  const canEditPrices = canEditTicketGridPrices(userType);
  const canManageReport = canManageTicketGridReport(userType);
  const [data, setData] = useState<IGenerateReportJsonResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshDisabled, setRefreshDisabled] = useState(false);
  const [openingEditor, setOpeningEditor] = useState(false);
  const [appliedNightKeys, setAppliedNightKeys] = useState<string[]>([]);
  const [isPriceEditing, setIsPriceEditing] = useState(false);
  const [priceEditDraft, setPriceEditDraft] = useState<PriceEditDraft>({});
  const [savingPrices, setSavingPrices] = useState(false);
  const [refreshConfirmOpen, setRefreshConfirmOpen] = useState(false);
  const [clearPricesConfirmOpen, setClearPricesConfirmOpen] = useState(false);
  const [notesPanelOpen, setNotesPanelOpen] = useState(true);
  const [selectedDate, setSelectedDate] = useState(todayApiDate);
  const hasLoadedReportRef = useRef(false);

  const selectedDateObject = useMemo(
    () => new DateObject({ date: selectedDate, format: API_DATE_FORMAT }),
    [selectedDate],
  );

  const loadReport = useCallback(async () => {
    if (!id) {
      setError("Не указан идентификатор сетки");
      setLoading(false);
      return;
    }

    if (!hasLoadedReportRef.current) {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await endpoints.report.generateReportJson({
        id,
        date: selectedDate,
      });
      hasLoadedReportRef.current = true;
      setData(response);
    } catch {
      setError("Не удалось загрузить данные сетки");
      setData(null);
      hasLoadedReportRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [id, selectedDate]);

  useLayoutEffect(() => {
    setSelectedDate(todayApiDate());
    hasLoadedReportRef.current = false;
    setIsPriceEditing(false);
    setPriceEditDraft({});
  }, [id]);

  const handleRefreshReport = useCallback(async () => {
    if (!id || refreshDisabled || refreshing) {
      return;
    }

    setRefreshConfirmOpen(false);
    setRefreshing(true);

    try {
      const result = await endpoints.report.refreshReport({ id });

      if (result.kind === "started") {
        enqueueSnackbar("Выгрузки запущены, ожидайте…", { variant: "success" });
        return;
      }

      if (result.kind === "inProgress") {
        enqueueSnackbar("Выгрузки в работе, ожидайте завершения", {
          variant: "info",
        });
        setRefreshDisabled(true);
        return;
      }

      enqueueSnackbar(`Произошла ошибка [${result.statusCode}]`, {
        variant: "error",
      });
    } finally {
      setRefreshing(false);
    }
  }, [enqueueSnackbar, id, refreshDisabled, refreshing]);

  const handleEditTicketGrid = useCallback(async () => {
    if (!id || openingEditor) {
      return;
    }

    const gridId = parseInt(id, 10);
    if (Number.isNaN(gridId)) {
      return;
    }

    setOpeningEditor(true);

    try {
      let info: IReportInfo | undefined = ticketGridTemplates.find(
        (template) => Number(template.id) === gridId,
      );

      if (!info) {
        info = await dispatch(getTicketGridInfo(gridId)).unwrap();
      }

      if (!info) {
        enqueueSnackbar("Не удалось загрузить данные сетки", { variant: "error" });
        return;
      }

      dispatch(
        showModal({
          type: "edit",
          info,
          id: info.id,
          kind: "ticketGrid",
        }),
      );
    } catch {
      enqueueSnackbar("Не удалось загрузить данные сетки", { variant: "error" });
    } finally {
      setOpeningEditor(false);
    }
  }, [dispatch, enqueueSnackbar, id, openingEditor, ticketGridTemplates]);

  const availableNights = useMemo(
    () => (data ? parseAvailableNights(data.nights) : []),
    [data],
  );

  const operatorNames = useMemo(
    () => (data ? getOperatorNamesFromStats(data.stats) : []),
    [data],
  );

  const handleStartPriceEditing = useCallback(() => {
    if (!data) {
      return;
    }

    setPriceEditDraft(
      buildPriceEditDraft(data, appliedNightKeys, availableNights),
    );
    setIsPriceEditing(true);
  }, [appliedNightKeys, availableNights, data]);

  const handleCancelPriceEditing = useCallback(() => {
    setIsPriceEditing(false);
    setPriceEditDraft({});
  }, []);

  const handleClearPrices = useCallback(() => {
    if (!data) {
      return;
    }

    setPriceEditDraft(buildEmptyPriceEditDraft(data));
    setClearPricesConfirmOpen(false);
  }, [data]);

  const handlePriceChange = useCallback(
    (date: string, operatorName: string, value: string) => {
      setPriceEditDraft((current) => ({
        ...current,
        [date]: {
          ...current[date],
          [operatorName]: value,
        },
      }));
    },
    [],
  );

  const handleSavePrices = useCallback(async () => {
    if (!id || !data || savingPrices) {
      return;
    }

    setSavingPrices(true);

    try {
      const payload = buildPriceEditPayload(
        priceEditDraft,
        data,
        appliedNightKeys,
      );
      const response = await endpoints.report.editReportPrices({
        id,
        prices: payload,
        ...(isPastApiDate(selectedDate) ? { date: selectedDate } : {}),
      });

      if (response.error) {
        enqueueSnackbar(response.error, { variant: "error" });
        return;
      }

      const refreshedData = await endpoints.report.generateReportJson({
        id,
        date: selectedDate,
      });
      setData(refreshedData);
      setIsPriceEditing(false);
      setPriceEditDraft({});
      enqueueSnackbar("Цены успешно сохранены", { variant: "success" });
    } catch {
      enqueueSnackbar("Не удалось сохранить цены", { variant: "error" });
    } finally {
      setSavingPrices(false);
    }
  }, [
    appliedNightKeys,
    data,
    enqueueSnackbar,
    id,
    priceEditDraft,
    savingPrices,
    selectedDate,
  ]);

  const handleReportDateChange = useCallback(
    (value: Value) => {
      if (!value || Array.isArray(value)) {
        return;
      }

      const nextDate = (value as DateObject).format(API_DATE_FORMAT);
      if (nextDate === selectedDate || !isSelectableReportDate(nextDate)) {
        return;
      }

      setIsPriceEditing(false);
      setPriceEditDraft({});
      setSelectedDate(nextDate);
    },
    [selectedDate],
  );

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  const goBack = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    window.close();
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error ?? "Данные не найдены"}</Typography>
        <Button sx={{ mt: 2 }} onClick={goBack}>
          ← Вернуться назад
        </Button>
      </Box>
    );
  }

  const title = decodeHtmlEntities(data.title);
  const gridName = decodeHtmlEntities(data.name);
  const exportLink = endpoints.report.getExportReportPricesLink({ id: id! });

  const headerIconButtonSx = {
    color: reportTableTheme.darkHeaderText,
    backgroundColor: "rgba(255, 255, 255, 0.12)",
    borderRadius: 1,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
    },
    "&.Mui-disabled": {
      color: "rgba(255, 255, 255, 0.4)",
      backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
  } as const;

  return (
    <Box
      sx={{
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          backgroundColor: reportTableTheme.darkHeaderBg,
          color: reportTableTheme.darkHeaderText,
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
          <Button
            variant="text"
            size="small"
            onClick={goBack}
            sx={{
              color: reportTableTheme.darkHeaderText,
              textTransform: "none",
              p: 0,
              minWidth: 0,
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
            }}
          >
            ← Вернуться назад
          </Button>
        </Box>
        <Typography
          component="h1"
          sx={{
            flex: 1,
            textAlign: "center",
            fontWeight: 700,
            fontSize: "14px",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          {title} ( {gridName} )
        </Typography>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Typography variant="caption" sx={{ opacity: 0.85, whiteSpace: "nowrap" }}>
            Обновлено {data.last_update}
          </Typography>
          <Tooltip title="Экспорт">
            <span>
              <IconButton
                size="small"
                href={exportLink}
                title="Экспорт"
                sx={headerIconButtonSx}
              >
                <RiFileExcel2Fill size={18} />
              </IconButton>
            </span>
          </Tooltip>
          {canEditPrices ? (
            isPriceEditing ? (
              <>
                <Button
                  variant="contained"
                  size="small"
                  disabled={savingPrices}
                  onClick={handleCancelPriceEditing}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.12)",
                    color: reportTableTheme.darkHeaderText,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      boxShadow: "none",
                    },
                  }}
                >
                  Отмена
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={savingPrices}
                  onClick={() => setClearPricesConfirmOpen(true)}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.12)",
                    color: reportTableTheme.darkHeaderText,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      boxShadow: "none",
                    },
                  }}
                >
                  Очистить
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={savingPrices}
                  onClick={() => void handleSavePrices()}
                  sx={{
                    backgroundColor: Color.brand,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: Color.brand,
                      filter: "brightness(0.9)",
                      boxShadow: "none",
                    },
                  }}
                >
                  {savingPrices ? "Сохранение…" : "Сохранить"}
                </Button>
              </>
            ) : (
              <Tooltip title="Редактировать цены">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleStartPriceEditing}
                    sx={headerIconButtonSx}
                  >
                    <MdCreate size={18} />
                  </IconButton>
                </span>
              </Tooltip>
            )
          ) : null}
          <Tooltip title="Дата отчёта">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                "& .rmdp-container": {
                  display: "flex",
                },
              }}
            >
              <DatePicker
                value={selectedDateObject}
                onChange={handleReportDateChange}
                weekDays={weekDays}
                weekStartDayIndex={1}
                months={months}
                className="green"
                format={DISPLAY_DATE_FORMAT}
                editable={false}
                maxDate={new DateObject()}
                containerStyle={{
                  width: "auto",
                }}
                style={{
                  width: 108,
                  height: 28,
                  fontSize: 13,
                  fontWeight: 500,
                  textAlign: "center",
                  color: reportTableTheme.darkHeaderText,
                  backgroundColor: "rgba(255, 255, 255, 0.12)",
                  border: "none",
                  borderRadius: 4,
                  boxSizing: "border-box",
                  cursor: "pointer",
                }}
              />
            </Box>
          </Tooltip>
          {canManageReport ? (
            <Tooltip title="Редактировать">
              <span>
                <IconButton
                  size="small"
                  disabled={openingEditor}
                  onClick={() => void handleEditTicketGrid()}
                  sx={headerIconButtonSx}
                >
                  <MdSettings size={18} />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {canManageReport ? (
            <Button
              variant="contained"
              size="small"
              disabled={refreshDisabled || refreshing}
              onClick={() => setRefreshConfirmOpen(true)}
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.12)",
                color: reportTableTheme.darkHeaderText,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                  color: "rgba(255, 255, 255, 0.4)",
                },
              }}
            >
              Обновить
            </Button>
          ) : null}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            overflow: "auto",
          }}
        >
        <Box
          component="table"
          sx={{
            width: "100%",
            maxWidth: 1280,
            margin: "0 auto",
            tableLayout: "fixed",
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
        <Box component="thead">
          <Box component="tr">
            <Box
              component="th"
              sx={{
                ...headerCellSx,
                width: reportTableTheme.dateColumnWidth,
                minWidth: reportTableTheme.dateColumnWidth,
                maxWidth: reportTableTheme.dateColumnWidth,
                textAlign: "left",
              }}
            >
              Дата
            </Box>
            {operatorNames.map((operatorName, index) => (
              <Box
                key={operatorName}
                component="th"
                sx={{
                  ...operatorHeaderCellSx,
                  textAlign: index === operatorNames.length - 1 ? "right" : "center",
                }}
              >
                {operatorName}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {data.stats.map((row) => {
            const tezOperatorName = operatorNames[operatorNames.length - 1];
            const tezMinPrice =
              resolveOperatorCell(
                row.operators[tezOperatorName],
                appliedNightKeys,
                availableNights,
              )?.price ?? null;

            return (
              <Box component="tr" key={row.date} sx={reportRowHoverSx}>
                <Box
                  component="td"
                  sx={{
                    width: reportTableTheme.dateColumnWidth,
                    minWidth: reportTableTheme.dateColumnWidth,
                    maxWidth: reportTableTheme.dateColumnWidth,
                    ...reportBodyCellTdSx,
                    textAlign: "left",
                    ...reportCellBackground("#ffffff"),
                  }}
                >
                  <Box
                    sx={{
                      ...priceCellInnerSx,
                      display: "flex",
                      alignItems: "center",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#212121",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.date}
                  </Box>
                </Box>
                {operatorNames.map((operatorName, index) => {
                  const isOurPrice = index === operatorNames.length - 1;

                  return (
                    <TicketGridReportCell
                      key={`${row.date}-${operatorName}`}
                      variant={isOurPrice ? "ourPrice" : "competitor"}
                      cell={resolveOperatorCell(
                        row.operators[operatorName],
                        appliedNightKeys,
                        availableNights,
                        isOurPrice
                          ? undefined
                          : {
                              applyPriceDeltaBackground: true,
                              tezMinPrice,
                            },
                      )}
                      isEditing={isPriceEditing}
                      editValue={priceEditDraft[row.date]?.[operatorName] ?? ""}
                      onEditValueChange={(value) =>
                        handlePriceChange(row.date, operatorName, value)
                      }
                    />
                  );
                })}
              </Box>
            );
          })}
        </Box>
        {availableNights.length > 0 ? (
          <Box component="tfoot">
            <Box component="tr">
              <Box
                component="td"
                colSpan={operatorNames.length + 1}
                sx={footerCellSx}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <ToggleButtonGroup
                  value={appliedNightKeys}
                  onChange={(_event, newNights) => {
                    setAppliedNightKeys(newNights ?? []);
                  }}
                  exclusive={false}
                  size="small"
                  sx={{ flexWrap: "wrap", gap: 0.5 }}
                >
                  {availableNights.map((night) => (
                    <ToggleButton
                      key={night}
                      value={night}
                      sx={{
                        textTransform: "none",
                        px: 2,
                        borderColor: reportTableTheme.border,
                        "&.Mui-selected": {
                          backgroundColor: Color.brand,
                          color: "#ffffff",
                          "&:hover": {
                            backgroundColor: Color.brand,
                            filter: "brightness(0.9)",
                          },
                        },
                      }}
                    >
                      {night}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : null}
        </Box>
        </Box>

        <TicketGridReportNotesPanel
          reportId={id!}
          open={notesPanelOpen}
          onToggle={() => setNotesPanelOpen((current) => !current)}
        />
      </Box>

      <Dialog open={refreshConfirmOpen} onClose={() => setRefreshConfirmOpen(false)}>
        <DialogContent>
          <DialogContentText>Запустить выгрузки цен прямо сейчас?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setRefreshConfirmOpen(false)}>
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleRefreshReport()}
            disabled={refreshDisabled || refreshing}
          >
            Запустить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={clearPricesConfirmOpen}
        onClose={() => setClearPricesConfirmOpen(false)}
      >
        <DialogContent>
          <DialogContentText>
            Очистить все цены? Изменения не будут сохранены до нажатия «Сохранить».
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setClearPricesConfirmOpen(false)}>
            Отмена
          </Button>
          <Button variant="contained" onClick={handleClearPrices}>
            Очистить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
